/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, Popover, TableColumnsType, Tag } from "antd";
import { ArrowRightIcon, ClockIcon } from "../../../../../../atoms/CustomIcons";
import { useEffect, useState } from "react";
import TableUi from "./Table";
import { JobCostingType } from "../../../../ducks/types";
import MaterialsIcon from "../../../../../../atoms/CustomIcons/MaterialsIcon";
import ManHoursIcon from "../../../../../../atoms/CustomIcons/ManHoursIcon";

interface PropsType {
  jobCostingData?: JobCostingType[];
}

export default function TabJobCosting(props: PropsType) {
  const { jobCostingData } = props;
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [manHoursData, setManHoursData] = useState<any[]>([]);
  const [materialsData, setMaterialsData] = useState<any[]>([]);

  function transformJobCostings(data: any[]) {
    // Split into categories
    const manHours = data.filter((d) => d.type === "MAN_HOURS");
    const materials = data.filter((d) => d.type === "MATERIALS");

    const manHoursResult: any[] = [];
    const materialsResult: any[] = [];

    // Helper: group + sum by key
    const groupAndSum = (arr: any[], key: "crew_id" | "material_id") => {
      const map = new Map<string, any>();

      arr.forEach((item) => {
        const id = item[key] || "unknown";
        if (!map.has(id)) {
          map.set(id, { ...item });
        } else {
          const existing = map.get(id);

          existing.estimated_hrs_or_qty_value +=
            item.estimated_hrs_or_qty_value || 0;
          existing.estimated_cost += item.estimated_cost || 0;
          existing.projected_cost += item.projected_cost || 0;
          existing.to_date_cost += item.to_date_cost || 0;
          existing.total_price += item.total_price || 0;
          existing.projected_qty += item.projected_qty || 0;
          existing.unit_cost += item.unit_cost || 0;
          existing.to_date_hrs_or_qty_value +=
            item.to_date_hrs_or_qty_value || 0;
        }
      });

      return Array.from(map.values());
    };

    // --- Insert Man-hours section ---
    if (manHours.length > 0) {
      const groupedManHours = groupAndSum(manHours, "crew_id");

      //   {
      //     key: "1",
      //     direct_cost: "Man-hours",
      //     est_labor_rate: "-",
      //     est_hrs: "1,000h 0m",
      //     projected_hrs: "1,000h 0m",
      //     to_date_hrs: "600h 0m",
      //     est_cost: "$ 50,000",
      //     projected_cost: "$ 55,000",
      //     to_date_cost: "$ 30,000",
      //     total_price: "$ 95,103",
      //     projected_gm: "42%",
      //   },

      const totals = {
        name: "Man-hours",
        unit_cost: groupedManHours.reduce(
          (sum, i) => sum + (i.unit_cost || 0),
          0
        ),
        estimated_hrs_or_qty_value: groupedManHours.reduce(
          (sum, i) => sum + (i.estimated_hrs_or_qty_value || 0),
          0
        ),
        estimated_hrs_or_qty_unit:
          manHours.find((i) => i.estimated_hrs_or_qty_unit)
            ?.estimated_hrs_or_qty_unit || "",
        projected_qty: groupedManHours.reduce(
          (sum, i) => sum + (i.projected_qty || 0),
          0
        ),
        to_date_hrs_or_qty_value: groupedManHours.reduce(
          (sum, i) => sum + (i.to_date_hrs_or_qty_value || 0),
          0
        ),
        to_date_hrs_or_qty_unit:
          manHours.find((i) => i.to_date_hrs_or_qty_unit)
            ?.to_date_hrs_or_qty_unit || "",
        estimated_cost: groupedManHours.reduce(
          (sum, i) => sum + (i.estimated_cost || 0),
          0
        ),
        actual_cost: groupedManHours.reduce(
          (sum, i) => sum + (i.estimated_cost || 0),
          0
        ),
        projected_cost: groupedManHours.reduce(
          (sum, i) => sum + (i.projected_cost || 0),
          0
        ),
        to_date_cost: groupedManHours.reduce(
          (sum, i) => sum + (i.to_date_cost || 0),
          0
        ),
        total_price: groupedManHours.reduce(
          (sum, i) => sum + (i.total_price || 0),
          0
        ),
        projected_gross_margin_perc:
          manHours.find((i) => i.projected_gross_margin_perc)
            ?.projected_gross_margin_perc || "",
      };

      manHoursResult.push(totals, ...groupedManHours);
    }

    // --- Insert Materials section ---
    if (materials.length > 0) {
      const groupedMaterials = groupAndSum(materials, "material_id");

      const totals = {
        name: "Materials",
        unit_cost: groupedMaterials.reduce(
          (sum, i) => sum + (i.unit_cost || 0),
          0
        ),
        estimated_hrs_or_qty_value: groupedMaterials.reduce(
          (sum, i) => sum + (i.estimated_hrs_or_qty_value || 0),
          0
        ),
        estimated_hrs_or_qty_unit:
          materials.find((i) => i.estimated_hrs_or_qty_unit)
            ?.estimated_hrs_or_qty_unit || "",
        projected_qty: groupedMaterials.reduce(
          (sum, i) => sum + (i.projected_qty || 0),
          0
        ),
        to_date_hrs_or_qty_value: groupedMaterials.reduce(
          (sum, i) => sum + (i.to_date_hrs_or_qty_value || 0),
          0
        ),
        to_date_hrs_or_qty_unit:
          materials.find((i) => i.to_date_hrs_or_qty_unit)
            ?.to_date_hrs_or_qty_unit || "",
        estimated_cost: groupedMaterials.reduce(
          (sum, i) => sum + (i.estimated_cost || 0),
          0
        ),
        actual_cost: groupedMaterials.reduce(
          (sum, i) => sum + (i.estimated_cost || 0),
          0
        ),
        projected_cost: groupedMaterials.reduce(
          (sum, i) => sum + (i.projected_cost || 0),
          0
        ),
        to_date_cost: groupedMaterials.reduce(
          (sum, i) => sum + (i.to_date_cost || 0),
          0
        ),
        total_price: groupedMaterials.reduce(
          (sum, i) => sum + (i.total_price || 0),
          0
        ),
        projected_gross_margin_perc:
          materials.find((i) => i.projected_gross_margin_perc)
            ?.projected_gross_margin_perc || "",
      };

      materialsResult.push(totals, ...groupedMaterials);
    }

    return {
      manHours: manHoursResult,
      materials: materialsResult,
    };
  }

  useEffect(() => {
    if (jobCostingData && jobCostingData?.length > 0) {
      const grouped = transformJobCostings(jobCostingData);
      setMaterialsData(grouped.materials);
      setManHoursData(grouped.manHours);
    }
  }, [jobCostingData]);

  const content = (
    <div className="popover_content">
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <div className="calc_item">
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <span className="value"> 580 hrs </span>
            <span className="value_name"> Total </span>
          </div>
        </div>
        <div className="calc_item">
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <span className="value"> 470 hrs </span>
            <span className="value_name"> Direct time </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <span className="value"> 96 hrs </span>
            <span className="value_name"> Overtime </span>
          </div>
        </div>
        <div className="calc_item">
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <span className="value"> 10 hrs </span>
            <span className="value_name"> Drive time </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <span className="value"> 4 hrs </span>
            <span className="value_name"> Overtime </span>
          </div>
        </div>
      </div>
      <table className="costing_table_popover">
        <thead>
          <tr>
            <th>Work order</th>
            <th>Actual cost</th>
            <th>Reason</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Softscape installation</td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <ArrowRightIcon />
            </td>
          </tr>
          <tr>
            <td>Planting</td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <ArrowRightIcon />
            </td>
          </tr>
          <tr>
            <td>Furnishings</td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <Tag className="grey_tag_fill"> $80,000 </Tag>
            </td>
            <td>
              <ArrowRightIcon />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const columns: TableColumnsType = [
    {
      title: "Direct cost",
      dataIndex: "name",
      fixed: "left",
      width: 270,
      render: (text: string) =>
        text ? (
          text === "Man-hours" || text === "Materials" ? (
            <div className="bold_row">
              {text === "Man-hours" ? (
                <ManHoursIcon className="fixed_left_icon" />
              ) : (
                <MaterialsIcon className="fixed_left_icon" />
              )}
              {text}
            </div>
          ) : (
            <span> {text} </span>
          )
        ) : (
          "-"
        ),
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
    },
    {
      title: "Est. labor rate",
      dataIndex: "unit_cost",
      width: 120,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `$${Number(text?.toFixed(2))?.toLocaleString()}`
          )
        ) : (
          "-"
        ),
      align: "right",
      sorter: (a, b) => a?.unit_cost?.localeCompare(b?.unit_cost),
      className: "right-aligned-sorter",
    },
    {
      title: "Est. hrs",
      dataIndex: "estimated_hrs_or_qty_value",
      width: 160,
      render: (text, record) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `${Number(text?.toFixed(2))?.toLocaleString()} ${
              record?.estimated_hrs_or_qty_unit
            }`
          )
        ) : (
          "-"
        ),
      align: "right",
      className: "right-aligned-sorter",
      sorter: (a, b) =>
        a?.estimated_hrs_or_qty_value?.localeCompare(
          b?.estimated_hrs_or_qty_value
        ),
    },
    {
      title: "Projected hrs",
      dataIndex: "projected_qty",
      width: 160,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            text
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",

      sorter: (a, b) => a?.projected_qty?.localeCompare(b?.projected_qty),
    },
    {
      title: "To date hrs",
      dataIndex: "to_date_hrs_or_qty_value",
      width: 160,
      render: (text, record) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `${text} ${record?.to_date_hrs_or_qty_unit}`
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",

      sorter: (a, b) => a?.to_date_hrs_qty?.localeCompare(b?.to_date_hrs_qty),
    },
    {
      title: "Est. cost",
      dataIndex: "estimated_cost",
      width: 120,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `$${Number(text?.toFixed(2))?.toLocaleString()}`
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",
      sorter: (a, b) => a?.estimated_cost?.localeCompare(b?.estimated_cost),
    },
    {
      title: "Projected cost",
      dataIndex: "projected_cost",
      width: 150,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `$${Number(text?.toFixed(2))?.toLocaleString()}`
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",
      sorter: (a, b) => a?.projected_cost?.localeCompare(b?.projected_cost),
    },
    {
      title: "To date cost",
      dataIndex: "to_date_cost",
      width: 150,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `$${Number(text?.toFixed(2))?.toLocaleString()}`
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",
      sorter: (a, b) => a?.to_date_cost?.localeCompare(b?.to_date_cost),
    },
    {
      title: "Total price",
      dataIndex: "total_price",
      width: 120,
      render: (text) =>
        text ? (
          text === "empty_row" ? (
            <div style={{ height: 20 }} />
          ) : (
            `$${Number(text?.toFixed(2))?.toLocaleString()}`
          )
        ) : (
          "-"
        ),
      className: "right-aligned-sorter",
      align: "right",
      sorter: (a, b) => a?.total_price?.localeCompare(b?.total_price),
    },
    {
      title: "Projected GM, %",
      dataIndex: "projected_gross_margin_perc",
      width: 150,
      render: (text) =>
        text ? (
          <Popover
            overlayClassName="project_gm_popover"
            overlayStyle={{ width: 550 }}
            content={content}
            title="Labor hours to date"
            arrow={false}
          >
            <Tag className="grey_full_tag">{text}</Tag>
          </Popover>
        ) : (
          "-"
        ),
      // sorter: (a, b) => a?.projected_gm?.localeCompare(b?.projected_gm),
      align: "center",
    },
  ];

  // const dummyArray = [
  //   {
  //     key: "1",
  //     direct_cost: "Man-hours",
  //     est_labor_rate: "-",
  //     est_hrs: "1,000h 0m",
  //     projected_hrs: "1,000h 0m",
  //     to_date_hrs: "600h 0m",
  //     est_cost: "$ 50,000",
  //     projected_cost: "$ 55,000",
  //     to_date_cost: "$ 30,000",
  //     total_price: "$ 95,103",
  //     projected_gm: "42%",
  //   },
  //   {
  //     key: "2",
  //     direct_cost: "CN 1 - Asher L.",
  //     est_labor_rate: "$50.00 / hr",
  //     est_hrs: "980h 0m",
  //     projected_hrs: "1,080h 0m",
  //     to_date_hrs: "580h 0m",
  //     est_cost: "$ 49,000",
  //     projected_cost: "$ 54,000",
  //     to_date_cost: "$ 29,000",
  //     total_price: "$ 93,103",
  //     projected_gm: "42%",
  //   },
  //   {
  //     key: "3",
  //     direct_cost: "CN 2 - Carlos E.",
  //     est_labor_rate: "$50.00 / hr",
  //     est_hrs: "980h 0m",
  //     projected_hrs: "1,080h 0m",
  //     to_date_hrs: "580h 0m",
  //     est_cost: "$ 49,000",
  //     projected_cost: "$ 54,000",
  //     to_date_cost: "$ 29,000",
  //     total_price: "$ 93,103",
  //     projected_gm: "42%",
  //   },
  // ];

  console.log("MATERIALS table:", materialsData);
  console.log("MAN HOURS table:", manHoursData);

  return (
    <div className="dashboard-tab">
      <Card className="contracts_job_costing_card">
        <Card style={{ borderRadius: "16px", border: "unset" }}>
          <div>
            <h3>
              <ClockIcon /> Job costing
            </h3>
          </div>

          <TableUi
            columns={columns}
            data={{
              count: manHoursData?.length,
              data: manHoursData || [],
            }}
            pageSize={limit}
            setPageSize={setLimit}
            setCurrentPage={setPagination}
            currentPage={pagination}
          />

          <TableUi
            columns={columns}
            data={{
              count: materialsData?.length,
              data: materialsData || [],
            }}
            pageSize={limit}
            setPageSize={setLimit}
            setCurrentPage={setPagination}
            currentPage={pagination}
          />
        </Card>
      </Card>
    </div>
  );
}
